import { useEffect, useRef, useState } from 'react'
import moment from 'moment'
import { api } from 'core/api/ConnectionManager'
import helpers from 'core/helpers'
import { getURLSearchParamsByLocation } from 'features/routes/utils'
import { IUser } from '../../core/models/Users'

const queryFields = ['action', 'object', 'startDate', 'endDate', 'userId']
const HISTORY_PAGE_SIZE = 20
const MAX_HISTORY_IN_MEMORY = 1000

export interface IInstance {
    id: number
    title: string
    duration: number
    companyId: number
    createdAt: string
    deletedAt: string | null
    updatedAt: string
    applyToNewDigitalSignage: boolean
    broadcast?: any
    label?: string
    name?: string
    endDate: string
    startDate: string
    startTime: string
    endTime: string
}

interface IParsedQuery {
    [index: string]: any
}

export interface IHistoryItem {
    context: {
        instances: IInstance[]
    }
    id: number
    companyId: number
    userId: number
    action: string
    object: string
    createdAt: string
    user: IUser
    withDetail: boolean | null
}

const HistoryMethods = (p_: any) => {
    const [historyList, setHistoryList] = useState<IHistoryItem[]>([])
    const [total, setTotal] = useState(0)
    const [isLoading, setIsLoading] = useState(true)
    const [_parsedQuery, setParsedQuery] = useState({})
    const [detailsHistoryEventId, setDetailsHistoryEventId] = useState<number | null>(null)
    const [withUserTimezone, setWithUserTimezone] = useState<boolean>(false)
    const [hasMore, setHasMore] = useState(true)
    const historyListRef = useRef<IHistoryItem[]>([])
    const isMountedRef = useRef(true)
    const isLoadingRef = useRef(false)
    const requestIdRef = useRef(0)
    const prevQueryRef = useRef<any>(null)

    useEffect(() => {
        return () => {
            isMountedRef.current = false
            requestIdRef.current += 1
        }
    }, [])

    useEffect(() => {
        const listenersId: string[] = []

        api.addObserver(
            'userActionsHistoryCreated',
            (item: IHistoryItem) => {
                const next = [item, ...historyListRef.current.filter((entry) => entry.id !== item.id)].slice(
                    0,
                    MAX_HISTORY_IN_MEMORY
                )

                historyListRef.current = next
                setHistoryList(next)
                setTotal((current) => current + 1)
            },
            listenersId
        )

        return () => {
            listenersId.forEach((id: string) => api.removeObserver(id))
        }
    }, [])

    useEffect(() => {
        if (JSON.stringify(prevQueryRef.current) !== JSON.stringify(p_.location)) {
            parsedQuery(p_)
            prevQueryRef.current = p_.location
        }
    }, [p_.location])
    useEffect(() => {
        getHistory(false)
    }, [_parsedQuery])

    const parsedQuery = (p_: any) => {
        const locationQuery = getURLSearchParamsByLocation(p_.location)
        const query = helpers.getQuery(locationQuery, 'history')
        let parsedQuery: IParsedQuery = {}
        queryFields.forEach((field: string) => {
            if (query[field]) {
                if (field === 'startDate' || field === 'endDate') {
                    if (moment(query[field]).isValid()) {
                        parsedQuery[field] = moment(query[field])
                            .utc()
                            .set(
                                field === 'startDate'
                                    ? { hour: 0, minute: 0, second: 0 }
                                    : { hour: 23, minute: 59, second: 59 }
                            )
                            .format('YYYY-MM-DD HH:mm:ss')
                    }
                } else {
                    parsedQuery[field] = query[field].split('//')
                }
            }
        })

        historyListRef.current = []
        setHistoryList([])
        setTotal(0)
        setHasMore(true)
        setParsedQuery(parsedQuery)
    }
    const getHistory = (isLazyLoad: boolean) => {
        if (isLazyLoad && (isLoadingRef.current || !hasMore)) return

        const requestId = requestIdRef.current + 1
        const data = {
            offset: isLazyLoad ? historyListRef.current.length : 0,
            perPage: HISTORY_PAGE_SIZE,
            ..._parsedQuery,
        }

        requestIdRef.current = requestId
        isLoadingRef.current = true
        setIsLoading(true)

        api.send('getUsersActionsHistory', data)
            .then(
                (res: any) => {
                    if (!isMountedRef.current || requestId !== requestIdRef.current || !res?.data) return

                    const source = isLazyLoad ? [...historyListRef.current, ...res.data] : [...res.data]
                    const seen = new Set<number>()
                    const next = source
                        .filter((entry) => {
                            if (seen.has(entry.id)) return false

                            seen.add(entry.id)
                            return true
                        })
                        .slice(0, MAX_HISTORY_IN_MEMORY)

                    historyListRef.current = next
                    setHistoryList(next)
                    setTotal(res.total)
                    setHasMore(res.data.length > 0 && next.length < res.total && next.length < MAX_HISTORY_IN_MEMORY)
                },
                () => undefined
            )
            .then(() => {
                if (requestId !== requestIdRef.current) return

                isLoadingRef.current = false
                if (isMountedRef.current) setIsLoading(false)
            })
    }

    const onDetailsModalOpen = (id: number) => {
        setDetailsHistoryEventId(id)
    }

    const onDetailsModalClose = () => {
        setDetailsHistoryEventId(null)
    }

    const changeWithUserTimezone = (value: boolean) => {
        setWithUserTimezone(value)
    }

    return {
        historyList,
        total,
        hasMore,
        isLoading,
        _parsedQuery,
        getHistory,
        detailsHistoryEventId,
        onDetailsModalOpen,
        onDetailsModalClose,
        withUserTimezone,
        changeWithUserTimezone,
    }
}

export default HistoryMethods
